import React, { useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  Divider,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { BaseDialog } from '@components/common/BaseDialog';
import { PageCard } from '@components/common/PageCard';
import { ModalIndices, TabIndices } from '../../common/i-doc/i-doc.registry.ts';
import { toastService } from '../../services/toast.service';
import { adminQuestionnairesConstructionService } from '../../services/questionnaires-construction/questionnaires-construction.service';
import { getTemplateStats } from '../../services/questionnaire-analytics/questionnaire-analytics.service';
import { ConstructionTemplate } from '../../types/questionnaire-construction';
import { TemplateStatsDto, QuestionStat } from '../../types/questionnaire-analytics';

const QuestionRow: React.FC<{ q: QuestionStat }> = ({ q }) => (
  <Box sx={{ py: 2 }}>
    <Typography variant="subtitle2" fontWeight={600}>{q.caption}</Typography>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
      Відповіли: {q.answeredCount}
    </Typography>

    {q.options && (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {q.options.length === 0 ? (
          <Typography variant="body2" color="text.disabled">Немає варіантів</Typography>
        ) : q.options.map(opt => (
          <Box key={opt.value}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">{opt.caption}</Typography>
              <Typography variant="body2" color="text.secondary">{opt.percentage}% ({opt.count})</Typography>
            </Box>
            <LinearProgress variant="determinate" value={opt.percentage} sx={{ height: 6, borderRadius: 1 }} />
          </Box>
        ))}
      </Box>
    )}

    {q.numeric && (
      q.numeric.average === null ? (
        <Typography variant="body2" color="text.disabled">Немає числових відповідей</Typography>
      ) : (
        <Typography variant="body2" color="text.secondary">
          Середнє: {q.numeric.average} · мін: {q.numeric.min} · макс: {q.numeric.max}
        </Typography>
      )
    )}

    {q.text && (
      q.text.sampleAnswers.length === 0 ? (
        <Typography variant="body2" color="text.disabled">Ще немає відповідей</Typography>
      ) : (
        <Box sx={{ maxHeight: 180, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {q.text.sampleAnswers.map((a, i) => (
            <Typography key={i} variant="body2" color="text.secondary" sx={{ borderLeft: '2px solid', borderColor: 'divider', pl: 1 }}>
              {a}
            </Typography>
          ))}
          {q.text.truncated && (
            <Typography variant="caption" color="text.disabled">
              Показано {q.text.sampleAnswers.length} з {q.text.totalSurveys}
            </Typography>
          )}
        </Box>
      )
    )}
  </Box>
);

export const QuestionnaireAnalyticsTab: React.FC = () => {
  const [templates, setTemplates] = useState<ConstructionTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState<ConstructionTemplate | null>(null);
  const [stats, setStats] = useState<TemplateStatsDto | null>(null);
  const [loadingStats, setLoadingStats] = useState(false);

  useEffect(() => {
    adminQuestionnairesConstructionService.getTemplates()
      .then(setTemplates)
      .catch(() => toastService.error('Не вдалося завантажити опитувальники'))
      .finally(() => setLoading(false));
  }, []);

  const handleOpen = async (template: ConstructionTemplate) => {
    setSelectedTemplate(template);
    setStats(null);
    setLoadingStats(true);
    try {
      const data = await getTemplateStats(template.id);
      setStats(data);
    } catch {
      toastService.error('Не вдалося завантажити статистику опитування');
    } finally {
      setLoadingStats(false);
    }
  };

  return (
    <Box data-i-doc={TabIndices.ADMIN_QUIZ_BUILDER_ANALYTICS}>
      <PageCard>
        {loading ? (
          <Box display="flex" justifyContent="center" sx={{ mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Назва</TableCell>
                  <TableCell>Статус</TableCell>
                  <TableCell>Відповідей</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {templates.map(t => (
                  <TableRow key={t.id} hover onClick={() => handleOpen(t)} sx={{ cursor: 'pointer' }}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>{t.nestNameI18n?.uk || `Опитувальник #${t.id}`}</Typography>
                    </TableCell>
                    <TableCell>{t.status === 'published' ? 'Готово' : 'Чернетка'}</TableCell>
                    <TableCell>{t.surveysCount}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </PageCard>

      <BaseDialog
        open={!!selectedTemplate}
        onClose={() => setSelectedTemplate(null)}
        title={stats?.templateName ?? selectedTemplate?.nestNameI18n?.uk ?? 'Статистика'}
        iDoc={ModalIndices.QUESTIONNAIRE_ANALYTICS}
        maxWidth="md"
      >
        {loadingStats ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : stats ? (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Завершених опитувань: {stats.totalCompletedSurveys}
            </Typography>
            {stats.questions.length === 0 ? (
              <Typography color="text.secondary">У шаблоні немає питань.</Typography>
            ) : (
              stats.questions.map((q, i) => (
                <React.Fragment key={q.questionId}>
                  {i > 0 && <Divider />}
                  <QuestionRow q={q} />
                </React.Fragment>
              ))
            )}
          </Box>
        ) : null}
      </BaseDialog>
    </Box>
  );
};
